import { useSearch } from "@tanstack/react-router"
import { Button } from "design-system/atoms/Button"
import { Sheet, SheetTrigger } from "design-system/atoms/Sheet"
import { useStartTestingConversationMutation } from "modules/conversations/hooks/useStartTestingConversationMutation"
import { useTestingExperimentOptionsQuery } from "modules/conversations/hooks/useTestingExperimentOptionsQuery"
import { CustomerDetail, CustomerDetailTabEnum } from "modules/customers/components/Detail"
import { useCustomersQuery } from "modules/customers/hooks/useCustomersQuery"
import { useGenerateFakeCustomerMutation } from "modules/customers/hooks/useGenerateFakeCustomerMutation"
import { useDeveloperMode } from "modules/shared/hooks/useDeveloperMode"
import { useCallback, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

type Props = {
  campaignId: string
}

export function TestCustomer({ campaignId }: Props) {
  const searchParams = useSearch({ strict: false })
  const { developerMode } = useDeveloperMode()
  const {
    data: experimentOptions = [],
    isLoading: isOptionsLoading,
    isError: isOptionsError,
  } = useTestingExperimentOptionsQuery(developerMode)
  const [isOpen, setIsOpen] = useState<boolean>(searchParams?.openPlayground ?? false)
  const [customerId, setCustomerId] = useState<string | null>(null)
  const [currentTab, setCurrentTab] = useState<CustomerDetailTabEnum>("conversations")

  const [conversationId, setConversationId] = useState<string | null>(null)
  const [issueId, setIssueId] = useState<string | undefined>()

  const { mutateAsync: generateFakeCustomer, isPending: isGeneratingFakeCustomer } =
    useGenerateFakeCustomerMutation({
      campaignId,
    })
  const { mutateAsync: startTestingConversation, isPending: isStartTestingConversationPending } =
    useStartTestingConversationMutation({})
  const { data: fakeCustomers, isPending: isFakeCustomersPending } = useCustomersQuery({
    query: {
      page: 1,
      total: 1,
      filterBy: {
        isFake: true,
        campaignId,
      },
    },
  })
  const { t } = useTranslation()

  const onOpenTestCustomer = useCallback(async () => {
    if (isFakeCustomersPending || isOptionsLoading || (developerMode && isOptionsError)) {
      return
    }

    let customer = fakeCustomers?.at(0)

    setIsOpen(true)

    if (!customer) {
      const fakeCustomer = await generateFakeCustomer()
      const shouldChooseExperiment = developerMode && experimentOptions.length > 0

      if (!shouldChooseExperiment) {
        await startTestingConversation({
          campaignId,
          customerId: fakeCustomer.customer.id,
          experiment: developerMode ? null : undefined,
        })
      }
      customer = fakeCustomer.customer
    }

    setCustomerId(customer.id)
  }, [
    campaignId,
    developerMode,
    fakeCustomers,
    experimentOptions,
    isOptionsLoading,
    isOptionsError,
    isFakeCustomersPending,
    generateFakeCustomer,
    startTestingConversation,
  ])

  const onClose = () => {
    setIsOpen(false)
    setCustomerId(null)
    setConversationId(null)
    setIssueId(undefined)
  }

  const onConversationClick = useCallback((id?: string) => {
    setCurrentTab("conversations")
    setConversationId(id ?? null)
  }, [])

  useEffect(() => {
    if (!!searchParams?.openPlayground) {
      onOpenTestCustomer()
    }
  }, [searchParams?.openPlayground, onOpenTestCustomer])

  return (
    <Sheet
      open={isOpen}
      onOpenChange={(open) => {
        setIsOpen(open)
        if (!open) {
          onClose()
        }
      }}
    >
      <SheetTrigger asChild>
        <Button
          onClick={onOpenTestCustomer}
          variant="testing"
          disabled={
            isGeneratingFakeCustomer ||
            isFakeCustomersPending ||
            isOptionsLoading ||
            (developerMode && isOptionsError) ||
            isStartTestingConversationPending
          }
        >
          {t("campaigns.detail.playground")}
        </Button>
      </SheetTrigger>
      {customerId && (
        <CustomerDetail
          customerId={customerId}
          conversationId={conversationId ?? undefined}
          campaignId={campaignId}
          onClose={onClose}
          currentTab={currentTab}
          onTabChange={setCurrentTab}
          onConversationClick={onConversationClick}
          issueId={issueId}
          onIssueClick={(id, tab) => {
            setCurrentTab(tab)
            setIssueId(id)
          }}
          onArchivedCustomerRedirect={setCustomerId}
        />
      )}
    </Sheet>
  )
}
